import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MindMap from './components/MindMap';
import Sidebar from './components/Sidebar';
import { parseMarkdownToTree, convertTreeToMarkdown } from './utils/markdownParser';
import { convertImageToMarkdown } from './utils/importImage';

import { parseXMindToMarkdown } from './utils/xmindParser';
import { INITIAL_MARKDOWN, PALETTES } from './constants';
import { MindMapNode, ColorPalette, DeleteStrategy, MindMapHandle, SyncStatus } from './types';
// NOTE: Gemini API integration has been removed as requested by the user.

import { Language, translations } from './utils/translations';
import { AlertTriangle, LogIn, LogOut, BrainCircuit, ShieldAlert, RefreshCw, X } from 'lucide-react';
import { CloudModal } from './components/CloudModal';
import { auth, db, handleFirestoreError, OperationType, googleProvider, getEnvVar } from './firebase';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, getDocFromServer, query, collection, where, orderBy, limit, getDocs, onSnapshot } from 'firebase/firestore';

import { ConfirmDialog } from './components/ConfirmDialog';
import { useMindMapStore } from './store/useMindMapStore';
import { useFirebaseSync } from './hooks/useFirebaseSync';
import { useFileHandlers } from './hooks/useFileHandlers';

let renderCount = 0;
const App: React.FC = () => {
  console.log('App render:', ++renderCount);
  const store = useMindMapStore();
  const data = store.data; const setData = store.setData;
  const history = store.history; const setHistory = store.setHistory;
  const palette = store.palette; const setPalette = store.setPalette;
  const isDarkMode = store.isDarkMode; const setIsDarkMode = store.setIsDarkMode;
  const language = store.language; const setLanguage = store.setLanguage;
  const showEmoji = store.showEmoji; const setShowEmoji = store.setShowEmoji;
  const isMandalaMode = store.isMandalaMode; const setIsMandalaMode = store.setIsMandalaMode;
  const selectedNode = store.selectedNode; const setSelectedNode = store.setSelectedNode;
  const rearrangeTrigger = store.rearrangeTrigger; const setRearrangeTrigger = store.setRearrangeTrigger;
  const rawMarkdown = store.rawMarkdown; const setRawMarkdown = store.setRawMarkdown;
  const fileName = store.fileName; const setFileName = store.setFileName;
  const lastAddedNodeId = store.lastAddedNodeId; const setLastAddedNodeId = store.setLastAddedNodeId;
  const mountKey = store.mountKey; const setMountKey = store.setMountKey;
  const isDirty = store.isDirty; const setIsDirty = store.setIsDirty;
  const initialView = store.initialView; const setInitialView = store.setInitialView;
  
  const showCloudModal = store.showCloudModal; const setShowCloudModal = store.setShowCloudModal;
  const userId = store.userId; const setUserId = store.setUserId;
  const userName = store.userName; const setUserName = store.setUserName;
  const currentMindmapId = store.currentMindmapId; const setCurrentMindmapId = store.setCurrentMindmapId;
  const syncStatus = store.syncStatus; const setSyncStatus = store.setSyncStatus;
  const remoteConflict = store.remoteConflict; const setRemoteConflict = store.setRemoteConflict;
  const fileSizeWarning = store.fileSizeWarning; const setFileSizeWarning = store.setFileSizeWarning;
  const [asyncError, setAsyncError] = useState<Error | null>(null);
  const isAuthLoading = store.isAuthLoading; const setIsAuthLoading = store.setIsAuthLoading;
  const isAccessDenied = store.isAccessDenied; const setIsAccessDenied = store.setIsAccessDenied;
  const isImportingImage = store.isImportingImage; const setIsImportingImage = store.setIsImportingImage;
  const modifyData = store.modifyData;
  const handleUndo = store.handleUndo;

  // Replay State
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [isPlayingAuto, setIsPlayingAuto] = useState(false);
  
  const historyTimestamps = store.historyTimestamps || [];
  const lastModified = store.lastModified || Date.now();

  const snapshots = useMemo(() => {
    return data ? [...history, data] : [];
  }, [history, data]);
  
  const snapshotTimestamps = useMemo(() => {
    return [...historyTimestamps, lastModified];
  }, [historyTimestamps, lastModified]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (isReplaying && isPlayingAuto) {
      intervalId = setInterval(() => {
        setReplayIndex(prev => {
          if (prev < snapshots.length - 1) {
            return prev + 1;
          } else {
            setIsPlayingAuto(false);
            return prev;
          }
        });
      }, 800);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isReplaying, isPlayingAuto, snapshots.length]);

  // Dialog State
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const currentMindmapIdRef = useRef<string | null>(null);

  const handleToggleDarkMode = () => setIsDarkMode(!isDarkMode);
  const handleToggleLanguage = () => setLanguage(language === 'en' ? 'ja' : 'en');
  const handleToggleEmoji = () => setShowEmoji(!showEmoji);
  const handleToggleMandalaMode = () => setIsMandalaMode(!isMandalaMode);

  // Keep ref in sync with state
  useEffect(() => { currentMindmapIdRef.current = currentMindmapId; }, [currentMindmapId]);

  // Ref for accessing MindMap methods (export)
  const mindMapRef = useRef<MindMapHandle>(null);
  
  const executeWithCheck = (action: () => void) => {
    if (isDirty) {
      setPendingAction(() => action);
      setShowConfirmDialog(true);
    } else {
      action();
    }
  };

  const onConfirmAction = () => {
    if (pendingAction) pendingAction();
    setShowConfirmDialog(false);
    setPendingAction(null);
  };

  const onCancelAction = () => {
    setShowConfirmDialog(false);
    setPendingAction(null);
  };

  const {
    handleFileUpload,
    handleCreateNew,
    handleImageUpload,
    handleXMindUpload,
    handleReset,
    handleSave,
    handleExportImage
  } = useFileHandlers(mindMapRef, executeWithCheck);

  const { sessionId } = useFirebaseSync(handleFileUpload, () => mindMapRef.current?.getViewState());

  const handleReplay = useCallback(() => {
      if (snapshots.length <= 1) return;
      setIsReplaying(true);
      setReplayIndex(0);
      setIsPlayingAuto(true);
  }, [snapshots.length]);

  const handleNodeMove = (nodeId: string, x: number, y: number) => {
      if (x === 0 && y === 0) return;

      const updateRecursive = (node: MindMapNode, delta: {x: number, y: number}, isTarget: boolean, applyToChildren: boolean): MindMapNode => {
          let newNode = { ...node };

          if (node.id === nodeId) {
              newNode.offset = { x, y };
              if (newNode.children) {
                   newNode.children = newNode.children.map(c => updateRecursive(c, delta, false, !node.isFloating));
              }
              return newNode;
          }

          if (applyToChildren) {
              const cx = newNode.offset?.x || 0;
              const cy = newNode.offset?.y || 0;
              newNode.offset = { x: cx + delta.x, y: cy + delta.y };
          }

          if (newNode.children) {
              newNode.children = newNode.children.map(c => updateRecursive(c, delta, false, applyToChildren));
          }

          return newNode;
      };

      modifyData((prevData) => {
          let targetNode: MindMapNode | null = null;
          const findNode = (n: MindMapNode) => {
              if (n.id === nodeId) targetNode = n;
              if (!targetNode && n.children) n.children.forEach(findNode);
          };
          findNode(prevData);

          if (!targetNode) return prevData;

          const oldX = targetNode.offset?.x || 0;
          const oldY = targetNode.offset?.y || 0;
          const delta = { x: x - oldX, y: y - oldY };
          
          return updateRecursive(prevData, delta, false, false); 
      });
  };

  const handleBulkNodeMove = (updates: { [id: string]: { x: number, y: number } }) => {
    const update = (node: MindMapNode): MindMapNode => {
        let newNode = node;
        if (updates[node.id]) {
            newNode = { ...node, offset: updates[node.id] };
        }
        if (newNode.children) {
            return { ...node, children: newNode.children.map(update) };
        }
        return newNode;
    };
    modifyData(prev => update(prev));
  };

  const handleReparentNode = (
      nodeId: string, 
      newParentId: string, 
      initialOffset?: { x: number, y: number },
      quadrant?: 'right' | 'left' | 'top' | 'bottom',
      index?: number
  ) => {
      if (nodeId === 'root') return;
      
      modifyData((prevData) => {
          let nodeToMove: MindMapNode | null = null;
      
          const removeNode = (node: MindMapNode): MindMapNode => {
              if (!node.children) return node;
              const nodeIdx = node.children.findIndex(child => child.id === nodeId);
              if (nodeIdx !== -1) {
                  nodeToMove = node.children[nodeIdx];
                  const newChildren = [...node.children];
                  newChildren.splice(nodeIdx, 1);
                  return { ...node, children: newChildren };
              }
              return { ...node, children: node.children.map(removeNode) };
          };

          const dataWithoutNode = removeNode(prevData);
          if (!nodeToMove) return prevData; 
          
          // FIX: Only set isFloating to TRUE if initialOffset is specifically provided.
          // This allows standard child re-attachment (quadrant-based) to snap correctly.
          const isFloating = newParentId === 'root' && initialOffset !== undefined;
          
          const cleanNode = { ...nodeToMove, offset: initialOffset, isFloating };
          
          if (newParentId === 'root') {
              if (isFloating) {
                  delete cleanNode.quadrant;
              } else if (quadrant) {
                  cleanNode.quadrant = quadrant;
              }
          } else {
              delete cleanNode.quadrant;
          }

          const addNode = (node: MindMapNode): MindMapNode => {
              if (node.id === newParentId) {
                  const currentChildren = node.children ? [...node.children] : [];
                  
                  if (typeof index === 'number' && index >= 0 && index <= currentChildren.length) {
                      currentChildren.splice(index, 0, cleanNode);
                  } else {
                      currentChildren.push(cleanNode);
                  }
                  
                  return { 
                      ...node, 
                      children: currentChildren
                  };
              }
              if (node.children) {
                  return { ...node, children: node.children.map(addNode) };
              }
              return node;
          };

          return addNode(dataWithoutNode);
      });
  };

  const handleAddNode = (parentId: string) => {
      const newId = `node-${Date.now()}`;
      setLastAddedNodeId(newId);
      
      modifyData((prevData) => {
          let parentIsFloating = false;
          const findParent = (node: MindMapNode): boolean => {
              if (node.id === parentId) {
                  if (node.isFloating) parentIsFloating = true;
                  return true;
              }
              if (node.children) return node.children.some(findParent);
              return false;
          };
          findParent(prevData);
          
          let newQuadrant: 'right'|'left'|'top'|'bottom' = 'right';
          if (parentId === 'root' && !parentIsFloating) {
              const rootChildren = prevData.children || [];
              const counts = { right: 0, left: 0, top: 0, bottom: 0 };
              rootChildren.forEach(c => {
                  if (c.quadrant) counts[c.quadrant]++;
                  else if (!c.isFloating) counts['right']++; 
              });
              
              if (counts.right <= counts.left) newQuadrant = 'right';
              else newQuadrant = 'left';
              
              if (counts.right > 5 && counts.left > 5) {
                   if (counts.top <= counts.bottom && counts.top < counts.right) newQuadrant = 'top';
                   else if (counts.bottom < counts.right) newQuadrant = 'bottom';
              }
          }

          const add = (node: MindMapNode): MindMapNode => {
              if (node.id === parentId) {
                  const newNode: MindMapNode = {
                      id: newId, 
                      name: translations[language].newTopic, 
                      children: [],
                      isFloating: parentIsFloating
                  };
                  if (parentId === 'root' && !parentIsFloating) {
                      newNode.quadrant = newQuadrant;
                  }
                  return {
                      ...node,
                      isCollapsed: false,
                      children: [...(node.children || []), newNode]
                  };
              }
              if (node.children) {
                  return { ...node, children: node.children.map(add) };
              }
              return node;
          };
          return add(prevData);
      });
  };

  const handleAddFloatingNode = (x: number, y: number) => {
      const newId = `node-${Date.now()}`;
      setLastAddedNodeId(newId);

      modifyData((prevData) => {
          const newNode: MindMapNode = {
              id: newId,
              name: translations[language].newTopic, 
              children: [],
              isFloating: true,
              offset: { x, y }
          };

          const add = (node: MindMapNode): MindMapNode => {
              if (node.id === 'root') {
                  return {
                      ...node,
                      children: [...(node.children || []), newNode]
                  };
              }
              return node;
          };
          return add(prevData);
      });
  };

  const handleNodeAddedProcessed = useCallback(() => {
      setLastAddedNodeId(null);
  }, []);

  const handleDeleteNode = (nodeId: string, strategy: DeleteStrategy = 'cascade') => {
      if (nodeId === 'root') return;
      
      modifyData((prevData) => {
          let targetNodeOffset = { x: 0, y: 0 };
          const findNodeOffset = (node: MindMapNode): boolean => {
              if (node.id === nodeId) {
                  if (node.offset) targetNodeOffset = node.offset;
                  return true;
              }
              if (node.children) {
                  return node.children.some(findNodeOffset);
              }
              return false;
          };
          findNodeOffset(prevData);

          const processNode = (node: MindMapNode): MindMapNode => {
              if (!node.children) return node;
              const index = node.children.findIndex(c => c.id === nodeId);
              if (index !== -1) {
                  const deletedNode = node.children[index];
                  const grandChildren = deletedNode.children || [];
                  let replacements: MindMapNode[] = [];
                  switch (strategy) {
                      case 'cascade': replacements = []; break;
                      case 'reparent':
                          if (node.id === 'root') {
                              replacements = grandChildren.map(gc => ({ ...gc, quadrant: deletedNode.quadrant || 'right' }));
                          } else {
                              replacements = grandChildren;
                          }
                          break;
                      case 'float': replacements = []; break;
                  }
                  const newChildren = [...node.children.slice(0, index), ...replacements, ...node.children.slice(index + 1)];
                  return { ...node, children: newChildren };
              }
              return { ...node, children: node.children.map(processNode) };
          };
          
          let newData = processNode(prevData);

          if (strategy === 'float') {
              const findDeletedNode = (n: MindMapNode): MindMapNode | undefined => {
                  if (n.id === nodeId) return n;
                  if (n.children) {
                      for (const c of n.children) if (findDeletedNode(c)) return findDeletedNode(c);
                  }
                  return undefined;
              };
              const originalNode = findDeletedNode(prevData);
              if (originalNode && originalNode.children && originalNode.children.length > 0) {
                  const orphans = originalNode.children.map((child, i) => ({
                      ...child,
                      isFloating: true,
                      offset: { 
                          x: (targetNodeOffset.x || 0) + 50, 
                          y: (targetNodeOffset.y || 0) + (i * 60) 
                      }
                  }));
                  newData.children = [...(newData.children || []), ...orphans];
              }
          }
          return newData;
      });

      if (selectedNode?.id === nodeId) {
          setSelectedNode(null);
      }
  };

  const handleRearrange = () => {
     modifyData((prevData) => {
         const resetNode = (node: MindMapNode): MindMapNode => {
             const newNode = { ...node };
             if (!node.isFloating) delete newNode.offset;
             if (newNode.children) newNode.children = newNode.children.map(resetNode);
             return newNode;
         };
         return resetNode(prevData);
     });
     setRearrangeTrigger(prev => prev + 1);
  };

  const handleToggleCollapse = (nodeId: string) => {
      modifyData((prevData) => {
          const update = (node: MindMapNode): MindMapNode => {
              if (nodeId === 'root-left' && node.id === 'root') return { ...node, isCollapsedLeft: !node.isCollapsedLeft };
              if (nodeId === 'root-right' && node.id === 'root') return { ...node, isCollapsedRight: !node.isCollapsedRight };
              if (nodeId === 'root-top' && node.id === 'root') return { ...node, isCollapsedTop: !node.isCollapsedTop };
              if (nodeId === 'root-bottom' && node.id === 'root') return { ...node, isCollapsedBottom: !node.isCollapsedBottom };
              if (node.id === nodeId) return { ...node, isCollapsed: !node.isCollapsed };
              if (node.children) return { ...node, children: node.children.map(update) };
              return node;
          };
          return update(prevData);
      });
  };

  const handleRenameProject = (newTitle: string) => {
      const cleanTitle = newTitle.trim();
      if (!cleanTitle) return;
      const sanitized = cleanTitle.replace(/[/\\?%*:|"<>]/g, '_');
      setFileName(`${sanitized}.md`);
      modifyData((prevData) => {
          if (prevData && prevData.id === 'root') {
              return { ...prevData, name: cleanTitle };
          }
          return prevData;
      });
  };

  const handleUpdateNodeName = (nodeId: string, newName: string) => {
      modifyData((prevData) => {
          const update = (node: MindMapNode): MindMapNode => {
              if (node.id === nodeId) return { ...node, name: newName };
              if (node.children) return { ...node, children: node.children.map(update) };
              return node;
          };
          return update(prevData);
      });
      if (nodeId === 'root' && newName.trim()) {
          const cleanName = newName.trim().replace(/[/\\?%*:|"<>]/g, '_');
          setFileName(`${cleanName}.md`);
      }
  };

  const handleUpdateNodeDescription = (nodeId: string, description: string) => {
      modifyData((prevData) => {
          const update = (node: MindMapNode): MindMapNode => {
              if (node.id === nodeId) return { ...node, description };
              if (node.children) return { ...node, children: node.children.map(update) };
              return node;
          };
          return update(prevData);
      });
  };

  const handleNodeClick = (node: MindMapNode | null) => {
    setSelectedNode(node);
  };

  const handleExpandAll = () => {
    modifyData(prev => {
        const expand = (n: MindMapNode): MindMapNode => ({
            ...n,
            isCollapsed: false,
            children: n.children?.map(expand)
        });
        return expand(prev);
    });
  };

  const handleCollapseLeaves = () => {
      modifyData(prev => {
          const collapse = (n: MindMapNode): MindMapNode => {
               if (!n.children || n.children.length === 0) return n;
               const allChildrenAreLeaves = n.children.every(c => !c.children || c.children.length === 0);
               if (allChildrenAreLeaves) {
                   return { ...n, isCollapsed: true };
               }
               return { ...n, children: n.children.map(collapse) };
          };
          return collapse(prev);
      });
  };

  if (asyncError) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 text-center">
        <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-500 mb-6">
          <AlertTriangle className="w-12 h-12" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Application Error</h1>
        <p className="text-slate-600 dark:text-slate-400 max-w-md mb-8">
          Something went wrong while initializing the application. This is often due to missing Firestore indexes or a connection issue.
        </p>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 mb-8 w-full max-w-2xl overflow-auto text-left relative">
           {asyncError.message.includes('https://console.firebase.google.com') ? (
             <div>
               <p className="text-sm font-medium text-amber-600 dark:text-amber-500 mb-2">Firestore Index Required</p>
               <div className="bg-rose-50 dark:bg-rose-900/10 p-3 rounded-lg mb-4 text-xs font-mono text-rose-600 dark:text-rose-400 break-all">
                  {asyncError.message.split('https://')[0]}
               </div>
               <a 
                 href={`https://${asyncError.message.split('https://')[1].split(' ')[0]}`}
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold rounded-lg shadow-lg shadow-rose-500/20 transition-all no-underline"
               >
                 Create Index in Firebase Console
               </a>
             </div>
           ) : (
             <code className="text-xs text-rose-500 dark:text-rose-400 break-all whitespace-pre-wrap">
               {asyncError.message}
             </code>
           )}
        </div>

        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/30 transition-all"
        >
          Reload Application
        </button>
      </div>
    );
  }

  if (isAuthLoading) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-400">
        <div className="relative">
          <BrainCircuit className="w-16 h-16 text-indigo-500/20 animate-pulse" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
        <p className="mt-8 text-sm font-medium tracking-widest text-slate-500 animate-pulse uppercase">Initializing Mandala...</p>
      </div>
    );
  }

  if (isAccessDenied) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 text-center">
        <div className="p-4 bg-rose-100 dark:bg-rose-900/30 rounded-full text-rose-600 dark:text-rose-500 mb-6">
          <ShieldAlert className="w-12 h-12" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Access Denied</h1>
        <p className="text-slate-600 dark:text-slate-400 max-w-md mb-8">
           This application is restricted to authorized users only. Please sign in with an authorized account or contact the administrator.
        </p>
        <button 
          onClick={() => signOut(auth)}
          className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-lg shadow-red-500/30 transition-all"
        >
          <LogOut className="w-5 h-5" />
          Sign Out & Retry
        </button>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 text-center">
        <div className="mb-10 relative">
          <div className="absolute -inset-4 bg-indigo-500/20 blur-2xl rounded-full animate-pulse" />
          <BrainCircuit className="w-20 h-20 text-indigo-500 relative z-10" />
        </div>
        <h1 className="text-4xl font-extrabold text-slate-800 dark:text-slate-100 mb-2 tracking-tight">Mind Mandala</h1>
        <p className="text-slate-600 dark:text-slate-400 max-w-md mb-12 text-lg">
          Welcome to your visual thinking space. Please sign in with Google to continue.
        </p>
        <button 
          onClick={() => signInWithPopup(auth, googleProvider)}
          className="flex items-center gap-3 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-xl shadow-indigo-500/40 transform transition-all hover:scale-105 active:scale-95"
        >
          <LogIn className="w-6 h-6" />
          Sign in with Google
        </button>
        <p className="mt-8 text-xs text-slate-500 dark:text-slate-600">
          Secure Cloud Storage & Mandatory Authentication Active
        </p>
      </div>
    );
  }

  if (!data) return (
    <div className="w-full h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-400">
       <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
       <p className="animate-pulse">Loading Mind Map...</p>
    </div>
  );

  return (
    <div className="w-full h-[100dvh] relative flex overflow-hidden">
      <Sidebar 
        userName={userName}
        fileName={fileName}
        syncStatus={syncStatus}
        currentMindmapId={currentMindmapId}
        onFileUpload={handleFileUpload}

        onCreateNew={handleCreateNew}
        onSave={handleSave}
        onExportImage={handleExportImage}
        onRearrange={handleRearrange}
        onReset={handleReset}
        onExpandAll={handleExpandAll}
        onCollapseLeaves={handleCollapseLeaves}
        onUndo={handleUndo}
        onReplay={handleReplay}
        canUndo={history.length > 0}
        isReplaying={isReplaying}
        currentPalette={palette}
        onPaletteChange={setPalette}
        selectedNode={selectedNode}
        isDarkMode={isDarkMode}
        onToggleDarkMode={handleToggleDarkMode}
        language={language}
        onToggleLanguage={handleToggleLanguage}
        showEmoji={showEmoji}
        onToggleEmoji={handleToggleEmoji}
        isDirty={isDirty}
        isMandalaMode={isMandalaMode}
        onToggleMandalaMode={handleToggleMandalaMode}
        onOpenCloudModal={() => setShowCloudModal(true)}
        onRenameProject={handleRenameProject}
        onImageUpload={handleImageUpload}
        isImportingImage={isImportingImage}
        onXMindUpload={handleXMindUpload}
      />
      
      <div className="flex-1 relative h-full">
        <MindMap 
          key={mountKey}
          ref={mindMapRef}
          data={isReplaying && snapshots.length > 0 ? snapshots[replayIndex] : data}
          palette={palette}
          fileName={fileName}
          language={language}
          onNodeClick={handleNodeClick}
          onNodeMove={handleNodeMove}
          onBulkNodeMove={handleBulkNodeMove}
          onToggleCollapse={handleToggleCollapse}
          onUpdateNodeName={handleUpdateNodeName}
          onUpdateNodeDescription={handleUpdateNodeDescription}
          onReparentNode={handleReparentNode}
          onAddNode={handleAddNode}
          onAddFloatingNode={handleAddFloatingNode}
          onDeleteNode={handleDeleteNode}
          onNodeAddedProcessed={handleNodeAddedProcessed}
          selectedNodeId={selectedNode?.id}
          rearrangeTrigger={rearrangeTrigger}
          lastAddedNodeId={lastAddedNodeId}
          showEmoji={showEmoji}
          isMandalaMode={isMandalaMode}
          initialViewState={initialView}
          isReplaying={isReplaying}
        />
        {isReplaying && snapshots.length > 0 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-[600px] max-w-[90vw] bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-700 p-4 rounded-2xl shadow-2xl z-50 flex flex-col items-center">
             <div className="flex justify-between items-center w-full mb-3">
                 <div className="flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                     <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Playback Mode</span>
                 </div>
                 <button 
                     onClick={() => setIsReplaying(false)} 
                     className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                 >
                     <X className="w-5 h-5" />
                 </button>
             </div>
             <div className="flex items-center gap-4 w-full">
                 <button 
                    onClick={() => {
                        if (!isPlayingAuto && replayIndex >= snapshots.length - 1) {
                            setReplayIndex(0);
                        }
                        setIsPlayingAuto(!isPlayingAuto);
                    }}
                    className="p-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                 >
                    {isPlayingAuto ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        </svg>
                    )}
                 </button>
                 <div className="flex-1 flex flex-col relative">
                     <input 
                         type="range" 
                         min={0} 
                         max={snapshots.length - 1} 
                         value={replayIndex} 
                         onChange={(e) => {
                             setIsPlayingAuto(false);
                             setReplayIndex(Number(e.target.value));
                         }} 
                         className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                     />
                     <div className="flex justify-between mt-1 text-xs text-slate-500 font-mono">
                         <span>{new Date(snapshotTimestamps[0]).toLocaleTimeString()}</span>
                         <span className="font-bold text-indigo-600 dark:text-indigo-400">
                             {new Date(snapshotTimestamps[replayIndex]).toLocaleTimeString()}
                         </span>
                         <span>{new Date(snapshotTimestamps[snapshotTimestamps.length - 1]).toLocaleTimeString()}</span>
                     </div>
                 </div>
             </div>
          </div>
        )}
      </div>

      <ConfirmDialog 
         isOpen={showConfirmDialog}
         title={translations[language].unsavedChanges}
         message={translations[language].unsavedChanges}
         onConfirm={onConfirmAction}
         onCancel={onCancelAction}
         onSaveToCloud={() => {
           setShowConfirmDialog(false);
           setShowCloudModal(true);
         }}
         confirmText="Discard Changes"
         cancelText="Keep Editing"
      />

      <CloudModal
        isOpen={showCloudModal}
        onClose={() => setShowCloudModal(false)}
        language={language}
        currentData={data}
        currentPalette={palette}
        fileName={fileName}
        currentMindmapId={currentMindmapId}
        sessionId={sessionId}
        onSetCurrentMindmapId={(id) => { currentMindmapIdRef.current = id; setCurrentMindmapId(id); }}
        onLoadMindmap={(markdown, title, mapId) => handleFileUpload(markdown, title, true, undefined, false, mapId)}
        getViewState={() => mindMapRef.current?.getViewState()}
        onSaveSuccess={(savedTitle) => {
          if (savedTitle) {
            handleRenameProject(savedTitle);
          }
          setIsDirty(false);
          setSyncStatus('synced');
        }}
      />

      {/* Remote conflict warning banner */}
      {remoteConflict && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] w-full max-w-lg px-4">
          <div className="flex items-start gap-3 p-4 rounded-xl shadow-2xl border border-amber-400/40 bg-amber-950/90 backdrop-blur-md text-amber-100 animate-in slide-in-from-bottom-4 duration-300">
            <RefreshCw className="w-5 h-5 mt-0.5 text-amber-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-300 text-sm">Another device just saved this map</p>
              <p className="text-xs text-amber-400/80 mt-0.5">
                Your local edits and the remote version have diverged.
                Reloading will discard your unsaved changes.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <button
                onClick={() => {
                  handleFileUpload(remoteConflict.rawMarkdown, remoteConflict.title + '.md', true, undefined, false, currentMindmapId);
                  setRemoteConflict(null);
                }}
                className="px-3 py-1.5 text-xs font-medium bg-amber-500 hover:bg-amber-400 text-amber-950 rounded-lg transition-colors"
              >
                Reload
              </button>
              <button
                onClick={() => setRemoteConflict(null)}
                className="p-1.5 text-amber-400 hover:text-amber-200 rounded-lg transition-colors"
                title="Dismiss — your next save will overwrite the remote version"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Size Warning Banner */}
      {fileSizeWarning && !remoteConflict && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] w-full max-w-lg px-4">
          <div className="flex items-start gap-3 p-4 rounded-xl shadow-2xl border border-red-400/40 bg-red-950/90 backdrop-blur-md text-red-100 animate-in slide-in-from-bottom-4 duration-300">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-red-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-300 text-sm">File size is becoming large</p>
              <p className="text-xs text-red-400/80 mt-0.5">
                The mindmap is approaching the 1MB save limit. Try clearing some history or splitting it into smaller maps.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <button
                onClick={() => setFileSizeWarning(false)}
                className="p-1.5 text-red-400 hover:text-red-200 rounded-lg transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;